import { createFileRoute } from "@tanstack/react-router";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { getStripe, getStripeEnvironment, productPriceMap } from "@/lib/stripe";
import { useCart } from "@/hooks/use-cart";
import { usePromo } from "@/hooks/use-promo";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Paiement — angle-shop.com" },
      { name: "description", content: "Finalisez votre commande angle-shop.com en toute sécurité." },
    ],
  }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const { items } = useCart();
  const { appliedCode } = usePromo();
  const [ready, setReady] = useState(false);

  // Wait for cart hydration from localStorage
  useEffect(() => {
    if (items.length > 0) {
      setReady(true);
    }
  }, [items]);

  const fetchClientSecret = async (): Promise<string> => {
    const checkoutItems = items.map((item) => ({
      priceId: productPriceMap[item.product.id],
      productId: item.product.id,
      productName: item.product.name,
      quantity: item.quantity,
      unitPrice: item.product.price,
    }));

    // Required when a promo code is applied — the server enforces single-use
    // per email by writing to the promo_usages table.
    const { data: { user } } = await supabase.auth.getUser();
    const customerEmail = user?.email ?? undefined;

    const { data, error } = await supabase.functions.invoke("create-checkout", {
      headers: {
        "x-payments-env": getStripeEnvironment(),
      },
      body: {
        items: checkoutItems,
        promoCode: appliedCode,
        customerEmail,
        returnUrl: `${window.location.origin}/confirmation?session_id={CHECKOUT_SESSION_ID}`,
      },
    });

    if (error || !data?.clientSecret) {
      throw new Error(error?.message || "Failed to create checkout session");
    }
    return data.clientSecret;
  };

  return (
    <main className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h1 className="font-display text-3xl font-bold text-foreground tracking-tight mb-8">
          Secure Checkout
        </h1>
        <div className="bg-card rounded-2xl p-6 shadow-card">
          {ready ? (
            <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
